import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldQuestion, ArrowLeft, Send, CheckCircle2, Loader2, Lock } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import logo from '../assets/logo.png';
import CaptchaField, { captchaConfigured } from '../components/CaptchaField';

/**
 * SecurityRecoverPage — alternate password recovery flow when the user
 * can't access their email. Requires the user to have pre-configured 2
 * security questions on /perfil/seguridad.
 *
 * Steps:
 *   1. Enter username/email + captcha → backend returns the 2 question IDs.
 *   2. Show the 2 questions; user types answers + new password.
 *   3. Submit → backend verifies + sets the new password.
 */
export default function SecurityRecoverPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState([]);
  const [username, setUsername] = useState('');
  const [questionIds, setQuestionIds] = useState([]);
  const [answers, setAnswers] = useState(['', '']);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const captchaRef = useRef(null);
  const captchaRef2 = useRef(null);

  useEffect(() => {
    api.get('/auth/security-questions-catalog')
      .then((r) => setCatalog(r.data || []))
      .catch(() => setCatalog([]));
  }, []);

  const questionText = (qid) => {
    const q = catalog.find((c) => c.id === qid);
    return q ? q.es : `Pregunta #${qid}`;
  };

  async function step1Submit(e) {
    e.preventDefault();
    if (captchaConfigured && !captchaToken) {
      setError('Por favor completa la verificación anti-bots.');
      return;
    }
    setLoading(true); setError('');
    try {
      const { data } = await api.post('/auth/security-recover-info', {
        username: username.trim(),
        captcha_token: captchaToken || undefined,
      });
      setQuestionIds(data.question_ids || []);
      setCaptchaToken('');
      if (captchaRef.current) captchaRef.current.reset();
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error');
      setCaptchaToken('');
      if (captchaRef.current) captchaRef.current.reset();
    }
    setLoading(false);
  }

  async function step2Submit(e) {
    e.preventDefault();
    if (newPwd.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    if (newPwd !== confirmPwd) { setError('Las contraseñas no coinciden.'); return; }
    if (captchaConfigured && !captchaToken) { setError('Por favor completa la verificación anti-bots.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/security-recover', {
        username: username.trim(),
        answers: questionIds.map((qid, idx) => ({ question_id: qid, answer: answers[idx] })),
        new_password: newPwd,
        captcha_token: captchaToken || undefined,
      });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Error');
      setCaptchaToken('');
      if (captchaRef2.current) captchaRef2.current.reset();
    }
    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal-50 via-white to-cmic-50 flex flex-col" data-testid="security-recover-page">
      <div className="p-6">
        <Link to="/forgot-password" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-cmic-600 transition-colors">
          <ArrowLeft size={16} />Volver a recuperar por correo
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <img src={logo} alt="CMIC" className="h-16 w-auto mb-4" />
            <h1 className="text-2xl font-bold text-charcoal-800">Recuperación por preguntas</h1>
            <p className="text-sm text-charcoal-500 mt-1.5 text-center max-w-xs">
              Responde tus 2 preguntas de seguridad para crear una nueva contraseña sin pasar por el correo.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            {done ? (
              <div className="text-center py-6" data-testid="security-recover-success">
                <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h2 className="font-semibold text-charcoal-800 mb-2">Contraseña actualizada</h2>
                <p className="text-sm text-charcoal-600 mb-6">Te redirigiremos al inicio de sesión.</p>
              </div>
            ) : step === 1 ? (
              <form onSubmit={step1Submit} className="space-y-5" data-testid="security-recover-step1">
                <div>
                  <label className="block text-sm font-medium text-charcoal-700 mb-1.5">Usuario o correo</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    data-testid="recover-username"
                    autoFocus
                    placeholder="tu-usuario o tu-correo@…"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                </div>
                {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>}
                <CaptchaField ref={captchaRef} onToken={setCaptchaToken} className="" />
                <button type="submit" disabled={loading || !username.trim() || (captchaConfigured && !captchaToken)}
                  data-testid="recover-step1-submit"
                  className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-200 disabled:opacity-50">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <><ShieldQuestion size={16} />Continuar</>}
                </button>
              </form>
            ) : (
              <form onSubmit={step2Submit} className="space-y-5" data-testid="security-recover-step2">
                <div className="bg-cmic-50 border border-cmic-100 rounded-xl px-4 py-3 text-xs text-cmic-800">
                  Si la cuenta existe y configuró preguntas, te mostramos sus 2 preguntas a continuación. Responde ambas correctamente para cambiar tu contraseña.
                </div>
                {questionIds.map((qid, idx) => (
                  <div key={qid}>
                    <label className="block text-xs font-medium text-charcoal-700 mb-1.5">
                      Pregunta {idx + 1}: <span className="text-charcoal-900">{questionText(qid)}</span>
                    </label>
                    <input
                      type="text"
                      value={answers[idx]}
                      onChange={(e) => {
                        const next = [...answers]; next[idx] = e.target.value; setAnswers(next);
                      }}
                      required
                      data-testid={`recover-answer-${idx}`}
                      autoFocus={idx === 0}
                      autoComplete="off"
                      className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                    />
                  </div>
                ))}
                <div className="border-t border-gray-100 pt-4">
                  <label className="block text-xs font-medium text-charcoal-700 mb-1.5">Nueva contraseña</label>
                  <input
                    type="password"
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    required
                    minLength={8}
                    data-testid="recover-newpwd"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-charcoal-700 mb-1.5">Confirmar nueva contraseña</label>
                  <input
                    type="password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    required
                    minLength={8}
                    data-testid="recover-confirmpwd"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                </div>
                {error && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" data-testid="recover-error">{error}</div>}
                <CaptchaField ref={captchaRef2} onToken={setCaptchaToken} className="" />
                <button type="submit" disabled={loading || !answers[0] || !answers[1] || newPwd.length < 8 || (captchaConfigured && !captchaToken)}
                  data-testid="recover-step2-submit"
                  className="w-full flex items-center justify-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white font-semibold rounded-xl px-6 py-3.5 transition-all duration-200 disabled:opacity-50">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <><Lock size={16} />Cambiar contraseña</>}
                </button>
                <button type="button" onClick={() => { setStep(1); setError(''); }}
                  className="w-full text-xs text-charcoal-500 hover:text-cmic-600 mt-2">
                  ← Volver al paso anterior
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
